import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Box, Typography, Card, Stack, Grid, IconButton, Chip, Divider } from '@mui/material';
import { ArrowLeft, TrendingUp, FileText, Zap, Calendar, BarChart2, Target } from 'lucide-react';

function loadCampaigns() {
    try { return JSON.parse(localStorage.getItem('postpilot_campaigns') || '[]'); } catch { return []; }
}

export default function Insights() {
    const navigate = useNavigate();
    const { posts, usage } = useAppContext();
    const campaigns = loadCampaigns();

    const now = new Date();

    const insights = useMemo(() => {
        // Last 7 days
        const days = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(now);
            d.setDate(d.getDate() - (6 - i));
            d.setHours(0, 0, 0, 0);
            return d;
        });

        const postsPerDay = days.map(day => {
            const next = new Date(day); next.setDate(day.getDate() + 1);
            const count = posts.filter(p => {
                const d = new Date(p.createdAt);
                return d >= day && d < next;
            }).length;
            return { label: day.toLocaleDateString('en-US', { weekday: 'short' }), count };
        });

        const maxPerDay = Math.max(...postsPerDay.map(d => d.count), 1);

        // Post type breakdown
        const typeMap: Record<string, number> = {};
        posts.forEach(p => { typeMap[p.postType] = (typeMap[p.postType] || 0) + 1; });
        const typeBreakdown = Object.entries(typeMap).sort((a, b) => b[1] - a[1]);

        // Tone breakdown
        const toneMap: Record<string, number> = {};
        posts.forEach(p => { toneMap[p.tone] = (toneMap[p.tone] || 0) + 1; });
        const toneBreakdown = Object.entries(toneMap).sort((a, b) => b[1] - a[1]);

        // Top topics
        const topicMap: Record<string, number> = {};
        posts.forEach(p => { topicMap[p.topic] = (topicMap[p.topic] || 0) + 1; });
        const topTopics = Object.entries(topicMap).sort((a, b) => b[1] - a[1]).slice(0, 5);

        // This week vs last week
        const startOfThisWeek = new Date(now);
        startOfThisWeek.setDate(now.getDate() - now.getDay());
        startOfThisWeek.setHours(0, 0, 0, 0);
        const startOfLastWeek = new Date(startOfThisWeek);
        startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

        const thisWeek = posts.filter(p => new Date(p.createdAt) >= startOfThisWeek).length;
        const lastWeek = posts.filter(p => {
            const d = new Date(p.createdAt);
            return d >= startOfLastWeek && d < startOfThisWeek;
        }).length;

        const weekGrowth = lastWeek === 0
            ? (thisWeek > 0 ? 100 : 0)
            : Math.round(((thisWeek - lastWeek) / lastWeek) * 100);

        // Active campaigns
        const activeCampaigns = campaigns.filter((c: any) => c.status === 'Active').length;

        return { postsPerDay, maxPerDay, typeBreakdown, toneBreakdown, topTopics, thisWeek, lastWeek, weekGrowth, activeCampaigns };
    }, [posts, campaigns]);

    const COLORS = ['#4f46e5', '#7c3aed', '#ec4899', '#f59e0b', '#10b981'];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, borderRadius: 2 }}>
                    <ArrowLeft size={18} color="#334155" />
                </IconButton>
                <Box>
                    <Typography variant="h5" fontWeight={800} color="#111827" letterSpacing="-0.02em">Weekly Insights</Typography>
                    <Typography variant="body2" color="#64748b">Performance data based on your generated posts</Typography>
                </Box>
            </Box>

            {posts.length === 0 ? (
                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 8, textAlign: 'center' }}>
                    <BarChart2 size={48} color="#94a3b8" style={{ margin: '0 auto 16px' }} />
                    <Typography variant="h6" fontWeight={700} color="#111827" gutterBottom>No data yet</Typography>
                    <Typography variant="body2" color="#64748b">Generate your first post to start seeing insights here.</Typography>
                </Card>
            ) : (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {[
                            {
                                icon: <FileText size={20} color="#4f46e5" />,
                                label: 'Posts This Week',
                                value: insights.thisWeek,
                                sub: insights.weekGrowth >= 0
                                    ? `+${insights.weekGrowth}% vs last week`
                                    : `${insights.weekGrowth}% vs last week`,
                                up: insights.weekGrowth >= 0,
                            },
                            {
                                icon: <Zap size={20} color="#4f46e5" />,
                                label: 'Total Generated',
                                value: posts.length,
                                sub: `${usage.generationsUsed} this cycle`,
                                up: true,
                            },
                            {
                                icon: <Target size={20} color="#4f46e5" />,
                                label: 'Active Campaigns',
                                value: insights.activeCampaigns,
                                sub: `${campaigns.length} total`,
                                up: insights.activeCampaigns > 0,
                            },
                            {
                                icon: <Calendar size={20} color="#4f46e5" />,
                                label: 'Post Types Used',
                                value: insights.typeBreakdown.length,
                                sub: insights.typeBreakdown[0]?.[0] || '—',
                                up: true,
                            },
                        ].map((s, i) => (
                            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                    <Box sx={{ width: 40, height: 40, bgcolor: '#e0e7ff', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                        {s.icon}
                                    </Box>
                                    <Typography variant="caption" color="#64748b" fontWeight={600} display="block" gutterBottom>{s.label}</Typography>
                                    <Typography variant="h4" fontWeight={800} color="#111827" letterSpacing="-0.02em">{s.value}</Typography>
                                    <Typography variant="caption" fontWeight={700} color={s.up ? '#10b981' : '#ef4444'}>{s.sub}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container spacing={3}>
                        {/* Posts per day bar chart */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={3}>Posts Generated — Last 7 Days</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 140, px: 1 }}>
                                    {insights.postsPerDay.map((day, i) => (
                                        <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                            <Typography variant="caption" fontWeight={700} color="#4f46e5">
                                                {day.count > 0 ? day.count : ''}
                                            </Typography>
                                            <Box sx={{
                                                width: '100%',
                                                height: `${Math.max(4, (day.count / insights.maxPerDay) * 100)}px`,
                                                bgcolor: day.count > 0 ? '#4f46e5' : '#e2e8f0',
                                                borderRadius: '4px 4px 0 0',
                                                transition: 'height 0.3s ease',
                                                minHeight: 4,
                                            }} />
                                            <Typography variant="caption" color="#94a3b8" fontWeight={600}>{day.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Card>
                        </Grid>

                        {/* Post Type breakdown */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, height: '100%' }}>
                                <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>Post Types</Typography>
                                <Stack spacing={2}>
                                    {insights.typeBreakdown.map(([type, count], i) => (
                                        <Box key={type}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight={600} color="#334155">{type}</Typography>
                                                <Typography variant="body2" fontWeight={700} color="#111827">{count}</Typography>
                                            </Box>
                                            <Box sx={{ height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${(count / posts.length) * 100}%`, bgcolor: COLORS[i % COLORS.length], borderRadius: 3 }} />
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Card>
                        </Grid>

                        {/* Top Topics */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>Top Topics</Typography>
                                <Stack spacing={1.5}>
                                    {insights.topTopics.length > 0 ? insights.topTopics.map(([topic, count], i) => (
                                        <Box key={topic} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: COLORS[i % COLORS.length] + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography variant="caption" fontWeight={800} color={COLORS[i % COLORS.length]}>{i + 1}</Typography>
                                            </Box>
                                            <Typography variant="body2" fontWeight={600} color="#334155" sx={{ flexGrow: 1 }} noWrap>{topic}</Typography>
                                            <Chip label={`${count}x`} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 700, fontSize: '0.7rem' }} />
                                        </Box>
                                    )) : <Typography variant="body2" color="#94a3b8">No posts generated yet.</Typography>}
                                </Stack>
                            </Card>
                        </Grid>

                        {/* Tone breakdown */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                <Typography variant="subtitle1" fontWeight={700} color="#111827" mb={2}>Tone Distribution</Typography>
                                <Stack spacing={2}>
                                    {insights.toneBreakdown.length > 0 ? insights.toneBreakdown.map(([tone, count], i) => (
                                        <Box key={tone}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="body2" fontWeight={600} color="#334155">{tone}</Typography>
                                                <Typography variant="caption" fontWeight={700} color="#64748b">{Math.round((count / posts.length) * 100)}%</Typography>
                                            </Box>
                                            <Box sx={{ height: 6, bgcolor: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                                <Box sx={{ height: '100%', width: `${(count / posts.length) * 100}%`, bgcolor: COLORS[i % COLORS.length], borderRadius: 3 }} />
                                            </Box>
                                        </Box>
                                    )) : <Typography variant="body2" color="#94a3b8">No data yet.</Typography>}
                                </Stack>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
}
